import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { PageHeader } from "../../layout/AppLayout";
import { Alert, ErpTable } from "../../components/erp";
import { errorText } from "../../api/client";
import { organicCheckApi, OrganicCheckRow } from "./api";

const FIELDS: { key: keyof typeof EMPTY; label: string }[] = [
  { key: "CheckedCount", label: "Checked Count" }, { key: "BlackGills", label: "Black Gills" },
  { key: "Soft", label: "Soft" }, { key: "DroopingHead", label: "Drooping Head" },
  { key: "BlackSpot", label: "Black Spot" }, { key: "Broken", label: "Broken" },
  { key: "ImproperLegs", label: "Improper Legs" }, { key: "Bends", label: "Bends" },
  { key: "BigPieces", label: "Big Pieces" }, { key: "SmallPieces", label: "Small Pieces" },
];
const EMPTY = { CheckedCount: "", BlackGills: "", Soft: "", DroopingHead: "", BlackSpot: "", Broken: "", ImproperLegs: "", Bends: "", BigPieces: "", SmallPieces: "", FitForHonPack: "Yes", Remarks: "" };

/** Port of v3/QualityControl/RmReportCreate.php — up to 3 internal/organic checks per lot into QcRmOrgnDetails. */
export default function OrganicCheckCreate() {
  const [params] = useSearchParams();
  const lotId = params.get("LotId") || "";
  const qc = useQueryClient();
  const checks = useQuery({ queryKey: ["qc", "organic-checks", lotId], queryFn: () => organicCheckApi.list(lotId), enabled: !!lotId });
  const [form, setForm] = useState(EMPTY);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const count = checks.data?.Data?.length || 0;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true); setMsg(null);
    try {
      const r = await organicCheckApi.post(lotId, form);
      if (r.Error) setMsg({ ok: false, text: r.Error });
      else { setMsg({ ok: true, text: `Check ${r.ReportCount}/3 saved.${r.ReadyForVerification ? " Ready for verification." : ""}` }); setForm(EMPTY); qc.invalidateQueries({ queryKey: ["qc", "organic-checks", lotId] }); }
    } catch (ex) { setMsg({ ok: false, text: errorText(ex) }); } finally { setSaving(false); }
  };

  return (
    <>
      <PageHeader title="Internal Check Report" location="Quality Control" />
      <div className="card"><div className="card-body">
        <p className="mb-3">Lot ID: <strong data-testid="organic-check-lot-id">{lotId}</strong> — {count}/3 checks recorded.</p>
        <ErpTable<OrganicCheckRow> testId="organic-check-history" loading={checks.isLoading}
          error={checks.isError ? errorText(checks.error) : null} rows={checks.data?.Data || []}
          columns={[{ key: "ReportID", title: "#" }, { key: "PostedDate", title: "Date" }, { key: "CheckedCount", title: "Count" },
            { key: "BlackGills", title: "Black Gills" }, { key: "Soft", title: "Soft" }, { key: "FitForHonPack", title: "Fit For HonPack" }, { key: "Remarks", title: "Remarks" }]} />
        {count >= 3 ? (
          <Alert kind="success" testId="organic-check-ready">All 3 checks recorded. <Link to={`/v3/QualityControl/RmAnalysisPreview?LotId=${lotId}`} data-testid="go-to-verification">Go to verification &rarr;</Link></Alert>
        ) : (
          <form onSubmit={submit} className="mt-3" data-testid="organic-check-form">
            <div className="row">
              {FIELDS.map((f) => (
                <div className="form-group col-md-4 col-lg-3 mb-3" key={f.key}>
                  <label className="form-label">{f.label}</label>
                  <input className="form-control" value={form[f.key]} onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))} data-testid={`organic-check-${f.key}`} />
                </div>
              ))}
              <div className="form-group col-md-4 col-lg-3 mb-3">
                <label className="form-label">Fit For HonPack</label>
                <select className="form-select" value={form.FitForHonPack} onChange={(e) => setForm((s) => ({ ...s, FitForHonPack: e.target.value }))} data-testid="organic-check-FitForHonPack">
                  <option value="Yes">Yes</option><option value="No">No</option>
                </select>
              </div>
              <div className="form-group col-md-8 col-lg-6 mb-3">
                <label className="form-label">Remarks</label>
                <input className="form-control" value={form.Remarks} onChange={(e) => setForm((s) => ({ ...s, Remarks: e.target.value }))} data-testid="organic-check-Remarks" />
              </div>
            </div>
            {msg && <Alert kind={msg.ok ? "success" : "danger"} testId="organic-check-msg">{msg.text}</Alert>}
            <button className="btn btn-primary" type="submit" disabled={saving} data-testid="organic-check-submit">{saving ? "Saving..." : "Submit Result"}</button>
          </form>
        )}
      </div></div>
    </>
  );
}
